import axios, { type AxiosError, type AxiosInstance } from 'axios';
import { env } from './env';

/**
 * Backend error envelope, as produced by NestJS BusinessException +
 * apiError helper on the ISI-GMAO backend (Phase 5 i18n contract).
 *
 * Example payload:
 *   { error: { key: 'auth.invalidCredentials', params: {}, statusCode: 401 } }
 *
 * The frontend resolves `key` through i18next (`apiError.<key>`) so a
 * single backend response can be displayed in fr/en/es without any
 * server-side translation.
 */
export interface ApiErrorEnvelope {
  error: {
    key: string;
    params?: Record<string, unknown>;
    statusCode?: number;
  };
}

/**
 * The cookie that carries the JWT is set httpOnly by the backend; the
 * browser attaches it automatically as long as `withCredentials: true`.
 * Same-site cookie + CORS allow-credentials must be set server-side
 * (already configured on the NestJS backend).
 */
export const api: AxiosInstance = axios.create({
  baseURL: env.VITE_API_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

/**
 * Custom error type carrying the i18n key + params, suitable for a
 * react-query mutation onError handler (or any catch block).
 */
export class ApiError extends Error {
  readonly key: string;
  readonly statusCode?: number;
  readonly params?: Record<string, unknown>;

  constructor(envelope: ApiErrorEnvelope, statusCode?: number) {
    super(envelope.error.key);
    this.key = envelope.error.key;
    this.params = envelope.error.params;
    this.statusCode = envelope.error.statusCode ?? statusCode;
  }
}

/**
 * Detects the standard backend error envelope and re-throws a typed
 * ApiError with the i18n key. Falls back to a generic key when the
 * payload is missing or malformed (e.g. network error, gateway timeout).
 */
function handleAxiosError(err: AxiosError<ApiErrorEnvelope>): never {
  if (err.response?.data?.error?.key) {
    throw new ApiError(err.response.data, err.response.status);
  }
  if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
    throw new ApiError(
      { error: { key: 'apiError.network.timeout' } },
      err.response?.status,
    );
  }
  if (!err.response) {
    throw new ApiError(
      { error: { key: 'apiError.network.unreachable' } },
      undefined,
    );
  }
  throw new ApiError(
    { error: { key: 'apiError.unknown', params: { status: err.response.status } } },
    err.response.status,
  );
}

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorEnvelope>) => handleAxiosError(error),
);
